import * as React from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import {
  button,
  buttonPressShadow,
  buttonShadow,
  colors,
  duration,
  easing,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacing,
  spacing,
  subtleShadow,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { BUTTON_SIZES, BUTTON_VARIANTS } from './constants';

type BaseButtonProps = {
  size: BUTTON_SIZES;
  variant: BUTTON_VARIANTS;
  $loading?: boolean;
  onClick: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
};

type StaticFauxButtonProps = {
  size: BUTTON_SIZES;
  variant: BUTTON_VARIANTS;
  children?: React.ReactNode;
};

const BASE_DISABLED_STYLE = css`
  &&& {
    color: ${colors.textDisabled};
    cursor: not-allowed;
    pointer-events: none;

    :focus {
      :before {
        border-color: ${colors.borderEmphasis};
      }
    }

    svg:not(.shape) path {
      stroke: ${colors.iconDisabled};
    }
    svg.shape {
      color: ${colors.iconDisabled};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconDisabled};
      stroke: none;
    }
  }
`;

export const DISABLED_STYLE = css`
  ${BASE_DISABLED_STYLE}

  &&& {
    background: ${colors.backgroundDisabled};
    border-color: ${colors.borderPrimary};
    box-shadow: ${subtleShadow.boxShadow}; /** disabled ghost buttons get borders */
  }
`;

const TEXT_DISABLED_STYLE = css`
  ${BASE_DISABLED_STYLE}
`;

export const LOADING_STYLE = css`
  div {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
  span {
    visibility: hidden;
  }
  pointer-events: none;
`;

/**
 * Unlike the rest of the variants (where the spinner color matches that of the button text),
 * the secondary + secondary destructive variants have custom grey spinners.
 */
export const LOADING_VARIANT_STYLES: Partial<Record<BUTTON_VARIANTS, FlattenSimpleInterpolation>> =
  {
    [BUTTON_VARIANTS.SECONDARY]: css`
      circle {
        stroke: ${colors.iconSubtle};
      }
    `,
    [BUTTON_VARIANTS.SECONDARYDESTRUCTIVE]: css`
      circle {
        stroke: ${colors.iconDestructive};
      }
    `,
  };

export const SIZE_STYLES: Record<BUTTON_SIZES, FlattenSimpleInterpolation> = {
  [BUTTON_SIZES.LARGE]: css({
    ...button,
    height: pxToRem(40),
  }),
  [BUTTON_SIZES.MEDIUM]: css({
    ...button,
    height: pxToRem(36),
  }),
  [BUTTON_SIZES.SMALL]: css({
    ...button,
    height: pxToRem(32),
    columnGap: spacing.threeEights,
  }),
};

const TEXT_SIZE_STYLES = {
  [BUTTON_SIZES.LARGE]: css`
    line-height: ${spacing.plusHalf};
    font-size: ${fontSizes.s14};
    font-family: ${fonts.sansSerif};
    font-weight: ${fontWeights.medium};
    letter-spacing: ${letterSpacing.pos10};
  `,
  [BUTTON_SIZES.MEDIUM]: css`
    line-height: ${spacing.plusHalf};
    font-size: ${fontSizes.s14};
    font-family: ${fonts.sansSerif};
    font-weight: ${fontWeights.medium};
    letter-spacing: ${letterSpacing.pos10};
  `,
  [BUTTON_SIZES.SMALL]: css`
    line-height: ${spacing.base};
    font-size: ${fontSizes.s12};
    font-family: ${fonts.sansSerif};
    font-weight: ${fontWeights.medium};
    letter-spacing: ${letterSpacing.pos10};
  `,
};

const variantStyles = {
  [BUTTON_VARIANTS.PRIMARY]: {
    background: colors.backgroundSuccess,
    border: colors.borderSuccess,
    color: colors.textOnColor,
    hoverBackground: colors.backgroundInteractiveStrong,
    hoverBorder: colors.borderInteractiveStrong,
    hoverColor: colors.textOnColor,
    hoverIconColor: colors.iconOnColor,
    focusBorder: colors.green50,
    iconColor: colors.iconOnColor,
    boxShadow: buttonShadow.boxShadow,
  },
  [BUTTON_VARIANTS.SECONDARY]: {
    background: colors.backgroundPrimary,
    border: colors.borderPrimary,
    color: colors.textPrimary,
    boxShadow: subtleShadow.boxShadow,
    hoverBackground: colors.backgroundInteractive,
    hoverBorder: colors.borderInteractiveSubtle,
    hoverColor: colors.textLink,
    focusBorder: colors.iconInteractiveSubtle,
    iconColor: colors.iconPrimary,
    hoverIconColor: colors.iconInteractive,
  },
  [BUTTON_VARIANTS.SECONDARYDESTRUCTIVE]: {
    background: colors.backgroundPrimary,
    border: colors.borderDestructiveSubtle,
    color: colors.textDestructive,
    boxShadow: subtleShadow.boxShadow,
    hoverBackground: colors.salmon10,
    hoverBorder: colors.borderDestructive,
    focusBorder: colors.borderDestructive,
    iconColor: colors.iconDestructive,
    hoverColor: colors.textDestructive,
    hoverIconColor: colors.iconDestructive,
  },
  [BUTTON_VARIANTS.GHOST]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textSecondary,
    hoverBackground: colors.backgroundTertiary,
    hoverColor: colors.textPrimary,
    hoverBorder: 'none',
    focusBorder: colors.borderSuccess,
    iconColor: colors.iconPrimary,
    hoverIconColor: colors.iconPrimaryHover,
  },
  [BUTTON_VARIANTS.GHOSTINFO]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textInfo,
    hoverBackground: colors.cyan20,
    hoverColor: colors.textInfo,
    hoverIconColor: colors.iconInfo,
    hoverBorder: 'none',
    focusBorder: colors.borderInfo,
    iconColor: colors.iconInfo,
  },
  [BUTTON_VARIANTS.GHOSTSUCCESS]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textSuccess,
    hoverBackground: colors.green20,
    hoverColor: colors.textSuccess,
    hoverIconColor: colors.iconSuccess,
    hoverBorder: 'none',
    focusBorder: colors.borderSuccess,
    iconColor: colors.iconSuccess,
  },
  [BUTTON_VARIANTS.GHOSTWARNING]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textWarning,
    hoverBackground: colors.salmon20,
    hoverColor: colors.textWarning,
    hoverIconColor: colors.iconWarning,
    hoverBorder: 'none',
    focusBorder: colors.borderWarning,
    iconColor: colors.iconWarning,
  },
  [BUTTON_VARIANTS.GHOSTERROR]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textDestructive,
    hoverBackground: colors.red20,
    hoverColor: colors.textDestructive,
    hoverIconColor: colors.iconDestructive,
    hoverBorder: 'none',
    focusBorder: colors.borderDestructive,
    iconColor: colors.iconDestructive,
  },
  [BUTTON_VARIANTS.GHOSTINVERTED]: {
    background: 'none',
    boxShadow: 'none',
    border: 'none',
    color: colors.textOnColor,
    hoverBackground: colors.overlay,
    hoverColor: colors.textOnColor,
    hoverIconColor: colors.iconOnColor,
    hoverBorder: 'none',
    focusBorder: colors.borderOnColor,
    iconColor: colors.iconOnColor,
  },
  [BUTTON_VARIANTS.DESTRUCTIVE]: {
    background: colors.backgroundDestructive,
    border: colors.red50,
    color: colors.textOnColor,
    hoverBackground: colors.backgroundDestructiveStrong,
    hoverBorder: colors.borderDestructive,
    hoverColor: colors.textOnColor,
    hoverIconColor: colors.iconOnColor,
    focusBorder: colors.borderDestructive,
    iconColor: colors.iconOnColor,
    boxShadow: buttonShadow.boxShadow,
  },
  [BUTTON_VARIANTS.TEXT]: {
    background: 'transparent',
    border: 'none',
    color: colors.textInteractive,
    hoverColor: colors.textInteractiveHover,
    hoverBackground: 'transparent',
    hoverBorder: 'none',
    hoverIconColor: colors.iconInteractiveHover,
    focusBorder: colors.borderInteractive,
    iconColor: colors.iconInteractive,
    boxShadow: 'none',
  },
};

const generateVariantStyles = (variant: BUTTON_VARIANTS) => css`
  background: ${variantStyles[variant].background};
  border-color: ${variantStyles[variant].border};
  color: ${variantStyles[variant].color};
  ${variantStyles[variant].boxShadow && `box-shadow: ${variantStyles[variant].boxShadow};`}

  svg:not(.shape) path {
    stroke: ${variantStyles[variant].iconColor};
  }
  svg.shape {
    color: ${variantStyles[variant].iconColor};
  }
  svg[data-temporary-fix] path {
    fill: ${variantStyles[variant].iconColor};
    stroke: none;
  }

  :focus:before {
    border-color: ${variantStyles[variant].focusBorder};
  }

  :hover {
    background: ${variantStyles[variant].hoverBackground};
    border-color: ${variantStyles[variant].hoverBorder};
    color: ${variantStyles[variant].hoverColor};

    svg:not(.shape) path {
      stroke: ${variantStyles[variant].hoverIconColor};
    }
    svg.shape {
      color: ${variantStyles[variant].hoverIconColor};
    }
    svg[data-temporary-fix] path {
      fill: ${variantStyles[variant].hoverIconColor};
      stroke: none;
    }
  }

  ${
    variant !== BUTTON_VARIANTS.TEXT &&
    css`
    :active {
      transform: translateY(${spacing.single});
      box-shadow: ${buttonPressShadow.boxShadow};
    }
  `
  }
`;

export const VARIANT_STYLES: Record<BUTTON_VARIANTS, FlattenSimpleInterpolation> = {
  [BUTTON_VARIANTS.PRIMARY]: generateVariantStyles(BUTTON_VARIANTS.PRIMARY),
  [BUTTON_VARIANTS.SECONDARY]: generateVariantStyles(BUTTON_VARIANTS.SECONDARY),
  [BUTTON_VARIANTS.SECONDARYDESTRUCTIVE]: generateVariantStyles(
    BUTTON_VARIANTS.SECONDARYDESTRUCTIVE,
  ),
  [BUTTON_VARIANTS.GHOST]: generateVariantStyles(BUTTON_VARIANTS.GHOST),
  [BUTTON_VARIANTS.GHOSTINFO]: generateVariantStyles(BUTTON_VARIANTS.GHOSTINFO),
  [BUTTON_VARIANTS.GHOSTSUCCESS]: generateVariantStyles(BUTTON_VARIANTS.GHOSTSUCCESS),
  [BUTTON_VARIANTS.GHOSTWARNING]: generateVariantStyles(BUTTON_VARIANTS.GHOSTWARNING),
  [BUTTON_VARIANTS.GHOSTERROR]: generateVariantStyles(BUTTON_VARIANTS.GHOSTERROR),
  [BUTTON_VARIANTS.GHOSTINVERTED]: generateVariantStyles(BUTTON_VARIANTS.GHOSTINVERTED),
  [BUTTON_VARIANTS.DESTRUCTIVE]: generateVariantStyles(BUTTON_VARIANTS.DESTRUCTIVE),
  [BUTTON_VARIANTS.TEXT]: generateVariantStyles(BUTTON_VARIANTS.TEXT),
};

export const baseButtonStyles = css`
  margin: 0;
  padding: 0 ${spacing.threeQuarter};
  box-shadow: ${buttonShadow.boxShadow};
  align-items: center;
  border-radius: ${spacing.quarter};
  border: solid ${spacing.single} transparent;
  /** space between interior adjacent elements */
  column-gap: ${spacing.half};
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  outline: none;
  overflow: visible;
  position: relative;
  transition: all ${duration.short} ${easing.entrance};
  z-index: 1;

  line-height: ${spacing.base};

  :before {
    border-style: solid;
    border-width: ${pxToRem(1.5)};
    border-radius: ${pxToRem(6)};
    bottom: -${spacing.quarter};
    content: '';
    display: block;
    left: -${spacing.quarter};
    opacity: 0;
    position: absolute;
    right: -${spacing.quarter};
    transition: all ${duration.short} ${easing.entrance};
    top: -${spacing.quarter};
    z-index: 0;
  }

  :focus-visible {
    :before {
      opacity: 1;
    }
  }
`;

export const createVariantStyles = (variant: BUTTON_VARIANTS) => VARIANT_STYLES[variant] || ``;
export const createSizeStyles = (variant: BUTTON_VARIANTS, size: BUTTON_SIZES) =>
  variant === BUTTON_VARIANTS.TEXT ? TEXT_SIZE_STYLES[size] : SIZE_STYLES[size] || ``;
export const createDisabledStyles = (isDisabled: boolean, variant: BUTTON_VARIANTS) =>
  isDisabled ? (variant === BUTTON_VARIANTS.TEXT && TEXT_DISABLED_STYLE) || DISABLED_STYLE : '';

export const BaseButton = styled.button<BaseButtonProps>`
  ${baseButtonStyles}

  ${({ variant, size }) => createSizeStyles(variant, size)};
  ${({ variant }) => createVariantStyles(variant)};
  ${({ $loading }) => $loading && LOADING_STYLE};
  ${({ $loading, variant }) => ($loading && LOADING_VARIANT_STYLES[variant]) || ``};

  /** the prop passed to the styled component is the HTML attr */
  ${({ 'aria-disabled': isDisabled, variant }) =>
    createDisabledStyles(Boolean(isDisabled), variant)}
`;

export const StaticFauxButton = styled.div<StaticFauxButtonProps>`
  ${baseButtonStyles}
  ${({ variant, size }) => createSizeStyles(variant, size)};
  ${({ variant }) => createVariantStyles(variant)};
`;
